import { ReactNode, useMemo, useRef, useState } from 'react'
import { Alert, Anchor, Box, Button, Group, Paper, SimpleGrid, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { EventForm } from 'components/events/EventForm/EventFormMain'
import { initialFormValues } from 'components/events/EventForm/initialFormValues'
import type {
  EventFormDeskMode,
  EventFormEvent,
  EventFormFieldId,
  EventFormValues
} from 'components/events/EventForm/types'
import { SendPushToBookmarks } from 'components/events/SendPushToBookmarks'
import { useDeskDecision } from 'hooks/desk/useDeskDecision'
import { useQueryCities } from 'hooks/cities'
import { useQueryTags } from 'hooks/events'
import { useQueryOrganizers } from 'hooks/organizers'
import { useQueryVenues } from 'hooks/venues'
import { useAppTranslation } from 'hooks/useTranslation'
import { ApplicationRow, EventRow, queryDeskCandidate, queryDeskTargetEvent } from 'supabase/desk'
import { DESK_MATCH_PANEL_ID } from './ChecksPanel'
import { CompareField, CompareRow, dateLocaleOf, fromApplication, fromEvent, proposalChanges, sideBySideRows } from './compareRows'
import { mapDeskError } from './deskErrors'
import { DeskFieldFlag, deskFlagStyle } from './DeskFieldFlag'
import { buildDeskPrefill, CityRow, DeskTag, timeNotStated, VenueRow } from './deskPrefill'
import { computeEditedFields, EditorBaseline, storagePathOf, toSubmitted } from './editedFields'
import {
  candidatePlaceFallback,
  cellText,
  COMPARE_LABEL_KEYS,
  eventPlaceFallback,
  eventTitleOf,
  placeLabelOf,
  showUndoToast,
  undoDecision
} from './MatchPanel'
import { buildPushDrafts, PushChange, PushDraftCandidate, PushDrafts, PushLanguage } from './pushDrafts'
import { showDeskError } from './RejectMenu'

type Translate = (key: string, options?: Record<string, unknown>) => string

/** The editor's reading of the post's status (import_metadata.editor.status, 0031). */
export type EditorStatus = 'scheduled' | 'cancelled' | 'postponed' | 'rescheduled'

const EDITOR_STATUSES: readonly EditorStatus[] = ['scheduled', 'cancelled', 'postponed', 'rescheduled']

/** import_metadata is model-adjacent JSON: read the one known value, drop anything else. */
export const editorStatusOf = (row: Pick<ApplicationRow, 'import_metadata'>): EditorStatus | null => {
  const metadata = row.import_metadata as { editor?: { status?: unknown } } | null
  const status = metadata?.editor?.status
  return EDITOR_STATUSES.find((allowed) => allowed === status) ?? null
}

export type UpdateOverlay = {
  /** The event as the Apply form opens with it: its own values, the proposal on top. */
  event: EventFormEvent
  /** The form sections the proposal changed (the yellow flags). */
  fields: EventFormFieldId[]
  /** True when the proposal sets the existing Cancelled switch. */
  cancelled: boolean
}

export type BuildUpdateOverlayParams = {
  event: EventRow
  candidate: ApplicationRow
  /** proposalChanges(before = the event, after = the candidate). */
  changes: CompareField[]
  venues: VenueRow[]
  tags: DeskTag[]
  cities: CityRow[]
}

const NO_IMAGES = { image_path: null, extra_image_paths: [] }

/**
 * A date the post states without a time, on the event's own stored time
 * (UTC digits = Lisbon wall clock), e.g. "adiado para 14 de novembro" moves
 * a 21:00 event to 14 Nov 21:00 (review AD-WR-09). Null when either is
 * missing.
 */
const onEventTime = (date: string | null | undefined, stored: string | null | undefined): string | null => {
  const day = date?.trim().slice(0, 10)
  if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return null
  const time = stored?.match(/T(\d{2}:\d{2})(:\d{2})?/)
  if (!time) return null
  return `${day}T${time[1]}${time[2] ?? ':00'}+00:00`
}

/**
 * The event overlaid by the proposal (D-15). Only a changed row the post
 * actually states is copied: a post that gives no price or no time never
 * blanks the event's own (the before/after still lists the row, so she sees
 * it). The candidate's values go through buildDeskPrefill, the same transform
 * the approve form uses, so a time, a venue or a language side reads the same
 * on both desk forms:
 *
 * - title: the candidate's language side only; the other stays the event's;
 * - start / end: the wall-clock datetime when both date and time are known; a
 *   date with no time keeps the event's own time on the new date. A changed
 *   start or end is always flagged, even when it cannot be pre-filled, so it
 *   is never saved unseen (review AD-WR-09);
 * - place: the venue (or the custom venue text) and the town;
 * - price: the stated price on both sides ('0' = free);
 * - status: a cancellation sets `cancelled`; a postponement has no event flag
 *   (it shows in the before/after and drafts the postponed push).
 *
 * Pure: no Supabase, no React.
 */
export const buildUpdateOverlay = ({
  event,
  candidate,
  changes,
  venues,
  tags,
  cities
}: BuildUpdateOverlayParams): UpdateOverlay => {
  const changed = new Set(changes)
  const prefill = buildDeskPrefill(candidate, { venues, tags, cities, publicUrl: () => '', rawItem: NO_IMAGES })
  const venueRow = event.venue === null ? undefined : venues.find((venue) => venue.id === event.venue)
  // The events row's `tags` holds ids, the form's `tags` holds tag rows; the
  // form reads the main tag from `main_tag`, so the id list stays out.
  const { tags: _tagIds, ...columns } = event
  const overlay: EventFormEvent = { ...columns, venues: venueRow }
  const fields: EventFormFieldId[] = []

  if (changed.has('title') && candidate.event_title?.trim()) {
    if (prefill.title) overlay.title = prefill.title
    if (prefill.title_pt) overlay.title_pt = prefill.title_pt
    fields.push('title')
  }
  // The approve form's placeholder time (00:00 / 23:59) is never used here:
  // a date with no stated time keeps the event's own time.
  const missingTime = timeNotStated(candidate)
  if (changed.has('start')) {
    const start = missingTime.start
      ? onEventTime(candidate.starting_date, event.start_time)
      : prefill.start_time
    if (start) overlay.start_time = start
    fields.push('start')
  }
  if (changed.has('end')) {
    const end = missingTime.end
      ? onEventTime(candidate.ending_date, event.finish_time)
      : prefill.finish_time
    if (end) overlay.finish_time = end
    fields.push('end')
  }
  const statesPlace =
    candidate.venue_id !== null || candidate.city_id !== null || candidatePlaceFallback(candidate) !== null
  if (changed.has('place') && statesPlace) {
    overlay.venue = prefill.venue ?? null
    overlay.venues = prefill.venues
    overlay.venue_name = prefill.venue_name ?? null
    overlay.venue_address = prefill.venue_address ?? null
    overlay.venue_city = prefill.venue_city ?? null
    overlay.venue_description = prefill.venue_description ?? null
    overlay.venue_location_lat = prefill.venue_location_lat ?? null
    overlay.venue_location_long = prefill.venue_location_long ?? null
    overlay.city_id = prefill.city_id ?? null
    fields.push('place')
  }
  const price = candidate.price?.trim()
  if (changed.has('price') && price) {
    const saved = price === '0' ? null : price
    overlay.price = saved
    overlay.price_pt = saved
    fields.push('price')
  }
  const cancelled = changed.has('status') && editorStatusOf(candidate) === 'cancelled'
  if (cancelled) overlay.cancelled = true

  return { event: overlay, fields, cancelled }
}

/**
 * A stored or saved instant as a wall-clock Date (pushDrafts' rule), to the
 * minute. Event times are Lisbon wall clock stamped +00:00 and the form's
 * pickers run in UTC, so the UTC digits ARE the wall clock.
 */
const wallClockOf = (value: Date | string | null | undefined): Date | null => {
  if (value === null || value === undefined || value === '') return null
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes())
}

export type SavedPushDraftParams = {
  /** What she saved in the Apply form. */
  saved: EventFormValues
  /** The published event as it was before the save. */
  event: EventRow
  /** The proposal's own changes (proposalChanges). */
  proposalChanges: CompareField[]
  /** The editor's reading of the post's status. */
  editorStatus: EditorStatus | null
  /** The event's place as the before/after showed it. */
  beforePlace: string | null
  venues: VenueRow[]
  cities: CityRow[]
  customVenueKeyword: string
  t: (key: string, vars: Record<string, string>, lng: PushLanguage) => string
}

/**
 * The drafted push from the values she SAVED (review AD-WR-08), not the
 * editor's proposal: she can correct the date, the place, the title or the
 * Cancelled switch in the Apply form, and every bookmarker reads the push.
 * Each change is measured against the event as it was before the save:
 *
 * - start: the saved start differs from the event's, to the minute;
 * - place: the saved venue (or custom venue) label differs from the event's;
 * - status: cancelled only when she saved Cancelled on an event that was not;
 *   postponed when the editor read a postponement and the proposal said so;
 * - title: the saved title, each language falling back to the other.
 */
export const savedPushDrafts = ({
  saved,
  event,
  proposalChanges,
  editorStatus,
  beforePlace,
  venues,
  cities,
  customVenueKeyword,
  t
}: SavedPushDraftParams): PushDrafts => {
  const changes = new Set<PushChange>(proposalChanges)

  const savedStart = wallClockOf(saved.startTime)
  const beforeStart = wallClockOf(event.start_time)
  if (savedStart && savedStart.getTime() !== beforeStart?.getTime()) changes.add('start')
  else changes.delete('start')

  const venueRow = saved.venue === customVenueKeyword ? undefined : venues.find((venue) => venue.name === saved.venue)
  const savedPlace = venueRow
    ? placeLabelOf(venueRow.id, venueRow.city_id ?? null, venueRow.name, venues, cities)
    : placeLabelOf(null, saved.city_id, saved.venue_name?.trim() || saved.venue_city?.trim() || null, venues, cities)
  if (savedPlace && savedPlace !== beforePlace) changes.add('place')
  else changes.delete('place')

  let status: PushDraftCandidate['status'] = 'scheduled'
  if (saved.cancelled === true && event.cancelled !== true) {
    status = 'cancelled'
    changes.add('status')
  } else if (editorStatus === 'postponed' && saved.cancelled !== true) {
    status = 'postponed'
  }

  return buildPushDrafts({
    changes: Array.from(changes),
    event: { title: saved.title, title_pt: saved.title_pt },
    candidate: { status, start: savedStart, place: savedPlace },
    t
  })
}

const ProposalRow = ({ row, t, afterStatusKey }: { row: CompareRow; t: Translate; afterStatusKey: string | null }) => {
  const cell = (side: 'before' | 'after', text: string) => (
    <Box
      data-testid={`update-cell-${side}`}
      data-highlight='changed'
      p={8}
      style={{ borderRadius: 4, background: 'var(--mantine-color-yellow-0)' }}
    >
      <Text size='xs' lh={1.4} c='dimmed'>
        {t(COMPARE_LABEL_KEYS[row.field])}
      </Text>
      <Text size='sm' style={{ whiteSpace: 'pre-wrap' }}>
        {text}
      </Text>
    </Box>
  )
  const after = row.field === 'status' && afterStatusKey ? t(afterStatusKey) : cellText(t, row.right)
  return (
    <SimpleGrid cols={2} spacing={16} data-testid={`update-row-${row.field}`}>
      {cell('before', cellText(t, row.left))}
      {cell('after', after)}
    </SimpleGrid>
  )
}

export type UpdateProposalPanelProps = {
  application: ApplicationRow
  /** After the decision is recorded: the screen opens the next update proposal. */
  onDecided: () => void
}

type View = 'proposal' | 'apply'

/**
 * The update proposal (DESK-04, D-15, UI-SPEC §F): before (the published
 * event) and after (this post) for the changed rows only, and two actions.
 *
 * **Apply** opens the EXISTING EventForm in event-edit mode (desk mode
 * 'apply'): it re-reads the candidate, saves through the existing
 * updateEvent and keeps the event's own picture (33-10). As soon as the save
 * succeeds it records `update_applied` once, with the fields she edited on
 * top of the proposal (her baseline is `initialFormValues` of the overlaid
 * event, the values she was shown). A list is always passed, never null:
 * 0035 refuses an applied update with no field list (23514). Only then does
 * the EXISTING SendPushToBookmarks modal open with the drafted en + pt-PT
 * messages; Send, Skip or an overlay close only end the push step and move
 * on. So leaving the page after the save never leaves an applied update
 * undecided, to be applied and pushed a second time (review AD-WR-10). The
 * desk writes no event and no notification itself (T-33-64).
 *
 * **Dismiss** records `update_dismissed` with an 8 s Undo and never touches
 * the event (T-33-67).
 */
export const UpdateProposalPanel = ({ application, onDecided }: UpdateProposalPanelProps) => {
  const { t, language } = useAppTranslation()
  const queryClient = useQueryClient()
  const customVenueKeyword = t('venues.custom')
  const { decideUpdate, undo } = useDeskDecision()
  const { venues, isError: venuesFailed } = useQueryVenues({})
  const { organizers, isError: organizersFailed } = useQueryOrganizers({ page: 1, perPage: 1000 })
  const { tags, isError: tagsFailed } = useQueryTags()
  const { cities, isError: citiesFailed } = useQueryCities()
  // AD-IN-03: a failed lookup is a load error, not "Loading…" forever.
  const lookupFailed = venuesFailed || organizersFailed || tagsFailed || citiesFailed

  const [view, setView] = useState<View>('proposal')
  const [pushOpen, setPushOpen] = useState(false)
  // Built from the saved values once the save is recorded (AD-WR-08).
  const [drafts, setDrafts] = useState<PushDrafts | null>(null)
  const [busy, setBusy] = useState(false)
  const savedValues = useRef<EventFormValues | null>(null)
  // Once per Apply: the record after the save, and the move on after the push.
  const recording = useRef(false)
  const movedOn = useRef(false)

  const id = application.id
  const eventId = application.match_target_event_id
  const targetEvent = useQuery({
    queryKey: ['desk', 'targetEvent', eventId],
    queryFn: () => queryDeskTargetEvent(eventId as number),
    enabled: eventId !== null
  })
  const event = targetEvent.data

  const status = editorStatusOf(application)
  const compare = useMemo(() => {
    if (!event) return null
    const before = fromEvent(event, placeLabelOf(event.venue, event.city_id, eventPlaceFallback(event), venues, cities))
    const after = fromApplication(
      application,
      placeLabelOf(application.venue_id, application.city_id, candidatePlaceFallback(application), venues, cities)
    )
    const rows = sideBySideRows(before, after, dateLocaleOf(language)).filter((row) => row.differs)
    return { rows, changes: proposalChanges(before, after), beforePlace: before.placeLabel }
  }, [event, application, venues, cities, language])

  const overlay = useMemo(
    () =>
      event && compare && venues && tags && cities
        ? buildUpdateOverlay({ event, candidate: application, changes: compare.changes, venues, tags, cities })
        : null,
    [event, compare, application, venues, tags, cities]
  )

  const failed = (error: unknown) => {
    showDeskError(t, error)
    if (mapDeskError(error).alreadyDecided) {
      void queryClient.invalidateQueries({ queryKey: ['desk', 'candidate', id] })
    }
  }

  if (targetEvent.isError || (lookupFailed && (!venues || !organizers || !tags || !cities))) {
    return <Alert color='red' title={t('desk.error.load')} />
  }
  if (!event || !compare || !overlay || !organizers || !tags || !venues) {
    return <Text size='sm'>{t('common.loading')}</Text>
  }

  const eventTitle = eventTitleOf(event, language) ?? t('desk.untitled')
  const pushT = (key: string, vars: Record<string, string>, lng: PushLanguage) => t(key, { ...vars, lng })

  // Pitfall 9: re-read before opening the form, so a candidate decided in
  // another tab never leads to an event update.
  const onApply = async () => {
    setBusy(true)
    try {
      const fresh = await queryClient.fetchQuery({
        queryKey: ['desk', 'candidate', id],
        queryFn: () => queryDeskCandidate(id),
        staleTime: 0
      })
      if (fresh.processed) {
        notifications.show({
          color: 'red',
          title: t('desk.error.already_decided_title'),
          message: t('desk.error.already_decided_body')
        })
        return
      }
    } catch (error) {
      showDeskError(t, error)
      return
    } finally {
      setBusy(false)
    }
    setView('apply')
  }

  const onDismiss = async () => {
    setBusy(true)
    let decidedAt: string
    try {
      decidedAt = await decideUpdate.mutateAsync({ id, decision: 'update_dismissed', editedFields: null })
    } catch (error) {
      setBusy(false)
      failed(error)
      return
    }
    setBusy(false)
    onDecided()
    showUndoToast({
      t,
      id,
      message: t('desk.update.dismissed'),
      onUndo: () => void undoDecision(t, undo, id, decidedAt)
    })
  }

  // Pitfall 9 again, at save time (AD-WR-10): the form can stay open while
  // the candidate is dismissed or decided in another tab.
  const beforeUpdate = async (): Promise<boolean> => {
    try {
      const fresh = await queryClient.fetchQuery({
        queryKey: ['desk', 'candidate', id],
        queryFn: () => queryDeskCandidate(id),
        staleTime: 0
      })
      if (!fresh.processed) return true
      notifications.show({
        color: 'red',
        title: t('desk.error.already_decided_title'),
        message: t('desk.error.already_decided_body')
      })
      return false
    } catch (error) {
      showDeskError(t, error)
      return false
    }
  }

  // Right after a successful save, before the push step (AD-WR-10). The
  // guard makes it once per Apply.
  const recordApplied = async (saved: EventFormValues) => {
    if (recording.current) return
    recording.current = true
    const baseline: EditorBaseline = {
      values: initialFormValues(overlay.event, { tags, language, isApplicationEdit: false, customVenueKeyword }),
      weekly: false,
      picturePath: storagePathOf(overlay.event.picture),
      customVenueKeyword
    }
    const editedFields = computeEditedFields(baseline, toSubmitted(saved, false))
    try {
      await decideUpdate.mutateAsync({ id, decision: 'update_applied', editedFields })
    } catch (error) {
      recording.current = false
      failed(error)
      // The event is saved but the decision is not, and no push was offered:
      // Apply again re-saves the same values and records it.
      setView('proposal')
      return
    }
    notifications.show({ color: 'green', message: t('desk.update.applied') })
    setDrafts(
      savedPushDrafts({
        saved,
        event,
        proposalChanges: compare.changes,
        editorStatus: status,
        beforePlace: compare.beforePlace,
        venues,
        cities: cities ?? [],
        customVenueKeyword,
        t: pushT
      })
    )
    setPushOpen(true)
  }

  // Send, Skip and an overlay close only end the push step; the decision is
  // already recorded.
  const endPush = () => {
    if (movedOn.current) return
    movedOn.current = true
    setPushOpen(false)
    onDecided()
  }


  const afterStatusKey =
    status && status !== 'scheduled' && application.route_checks.includes('status_change')
      ? `desk.status.${status}`
      : null

  const changedFields = new Set(overlay.fields)
  const renderField = (field: EventFormFieldId, node: ReactNode) =>
    changedFields.has(field) ? (
      <DeskFieldFlag field={field} checks={[]} variant='changed'>
        {node}
      </DeskFieldFlag>
    ) : (
      node
    )

  const deskMode: EventFormDeskMode = {
    mode: 'apply',
    submitLabel: t('desk.update.save'),
    backLabel: t('desk.update.back'),
    onBack: () => setView('proposal'),
    disableAiMatch: true,
    hidePushButton: true,
    renderField,
    beforeUpdate,
    onUpdated: (values) => {
      savedValues.current = values
      void recordApplied(values)
    },
    // AD-WR-07: the event may or may not have changed; say the save failed
    // and stay on the form so she can check and save again.
    onUpdateError: () => {
      notifications.show({ color: 'red', message: t('desk.update.save_failed') })
    }
  }

  const heading = (
    <Group gap={8} align='baseline' wrap='wrap'>
      <Text size='md' fw={600} lh={1.3}>
        {t('desk.update.title')}
      </Text>
      <Text size='xs' lh={1.4} c='dimmed'>
        {t('desk.update.for_event', { title: eventTitle })}
      </Text>
      {event.uid && (
        <Anchor component={Link} to={`/Events/${event.uid}`} size='sm'>
          {t('desk.match.open_event')}
        </Anchor>
      )}
    </Group>
  )

  const push = (
    <SendPushToBookmarks
      visible={pushOpen}
      eventId={event.id}
      desk
      initialEnglish={drafts?.en}
      initialPortuguese={drafts?.pt}
      onSkip={endPush}
      onSent={endPush}
      onClose={endPush}
    />
  )

  if (view === 'apply') {
    return (
      <Stack gap={16} id={DESK_MATCH_PANEL_ID}>
        <Text size='md' fw={600} lh={1.3}>
          {t('desk.update.updating', { title: eventTitle })}
        </Text>
        {overlay.cancelled && (
          <Box data-testid='update-status-flag' data-flag='changed' style={deskFlagStyle('changed')}>
            <Text size='xs' lh={1.4} c='yellow.9'>
              {t('desk.update.changed_field')}
            </Text>
            <Text size='sm'>
              {t('desk.fields.status')}: {t('desk.status.cancelled')}
            </Text>
          </Box>
        )}
        <EventForm
          key={`apply-${id}`}
          isNew={false}
          event={overlay.event}
          venues={venues}
          organizers={organizers}
          tags={tags}
          currOrganizerId={undefined}
          organizerVenues={[]}
          desk={deskMode}
        />
        {push}
      </Stack>
    )
  }

  return (
    <Paper id={DESK_MATCH_PANEL_ID} withBorder p={16}>
      <Stack gap={16}>
        {heading}
        {compare.rows.length === 0 ? (
          <Text size='sm' c='dimmed'>
            {t('desk.update.no_changes')}
          </Text>
        ) : (
          <Stack gap={8}>
            <SimpleGrid cols={2} spacing={16}>
              <Text size='md' fw={600} lh={1.3}>
                {t('desk.update.before')}
              </Text>
              <Text size='md' fw={600} lh={1.3}>
                {t('desk.update.after')}
              </Text>
            </SimpleGrid>
            {compare.rows.map((row) => (
              <ProposalRow key={row.field} row={row} t={t} afterStatusKey={afterStatusKey} />
            ))}
          </Stack>
        )}
        <Group justify='flex-end' gap={8}>
          <Button variant='default' onClick={onDismiss} disabled={busy}>
            {t('desk.update.dismiss')}
          </Button>
          <Button variant='filled' onClick={onApply} disabled={busy}>
            {t('desk.update.apply')}
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}
